import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { useAppSelector } from '../../../../../../../../../hooks';
import {
  COURSE_TUTOR_VERIFY_DELETE_TYPES,
  getCourseTutorsState,
  ICourseTutor,
  TCourseTutorVerifyDelete,
} from '../../../../../../../../../store/V2/CourseTutorsSlice';
import {
  deleteCourseZoomSectionByIdAsAdmin,
  ISection,
  updateAdminCourseSectionState,
  updateCourseZoomSectionAsAdmin,
} from '../../../../../../../../../store/V2/SectionSlice';
import UpcomingClassItem from './UpcomingClassItem';
import { ISelectedOption } from '../../../../../../../../../constants';
import DarkLoadingButton, {
  DarkButton,
  defaultButtonStyles,
} from '../../../../../../../../../components/V2/Buttons';

interface CourseTutorVerificationModalProps {
  open: boolean;
  modalType: TCourseTutorVerifyDelete;
  onClose: () => void;
  handleDeleteTutor: (courseTutor: ICourseTutor) => void;
  handleDisableTutor: (courseTutor: ICourseTutor) => void;
}

export interface ICustomUpcomingClassItem extends ISection {
  selectedTutorOption?: ISelectedOption | null;
  isDeleteChecked?: boolean;
  isLoading?: boolean;
  errorMessage?: string;
  successMessage?: string;
}

const CourseTutorVerificationModal: FunctionComponent<CourseTutorVerificationModalProps> = ({
  open,
  modalType,
  onClose,
  handleDeleteTutor,
  handleDisableTutor,
}) => {
  const dispatch = useDispatch();

  // Store
  const courseTutorsState = useAppSelector(getCourseTutorsState);

  // State
  const [isLoading, setLoading] = useState(false);
  const [tutorOptions, setTutorOptions] = useState<ISelectedOption[]>([]);
  const [upcomingList, setUpcomingList] = useState<ICustomUpcomingClassItem[]>(
    []
  );

  const {
    adminCourseTutorDeleteVerificationData,
    adminCourseTutorsData,
  } = courseTutorsState;

  let upcomingClassesCount = 0;
  let upcomingClasses: ISection[] = [];
  let courseTutor: ICourseTutor | undefined;
  let tutorName = '';
  let modalDescription = '';

  const courseTutors =
    adminCourseTutorsData &&
    adminCourseTutorsData.list &&
    adminCourseTutorsData.list.length
      ? adminCourseTutorsData.list
      : [];

  const isAllSuccess = upcomingList.every((item) => item.successMessage);
  const isAllNotUpdate = upcomingList.every(
    (item) => !item.selectedTutorOption && !item.isDeleteChecked
  );

  const isSubmitDisabled = isAllSuccess || isAllNotUpdate;

  if (adminCourseTutorDeleteVerificationData) {
    upcomingClasses = [
      ...(adminCourseTutorDeleteVerificationData.upcomingClasses || []),
    ];

    courseTutor = adminCourseTutorDeleteVerificationData.courseTutor;
    upcomingClassesCount =
      adminCourseTutorDeleteVerificationData.upcomingClassesCount || 0;
  }

  if (courseTutor && courseTutor.tutor) {
    tutorName = courseTutor.tutor.firstname;

    if (courseTutor.tutor.lastname) {
      tutorName += ` ${courseTutor.tutor.lastname}`;
    }
  }

  modalDescription = `${tutorName} has ${upcomingClassesCount} upcoming class${
    upcomingClassesCount > 1 ? 'es' : ''
  }. Please change the tutor / delete the class${
    upcomingClassesCount > 1 ? 'es' : ''
  }`;

  if (modalType === COURSE_TUTOR_VERIFY_DELETE_TYPES.DELETE) {
    modalDescription += ` before delete`;
  } else if (modalType === COURSE_TUTOR_VERIFY_DELETE_TYPES.DISABLE) {
    modalDescription += ` before disable`;
  }

  modalDescription += '.';

  useEffect(() => {
    const newUpcomingClasses = [...upcomingClasses].map((item) => {
      const newItem: ICustomUpcomingClassItem = { ...item };

      newItem.selectedTutorOption = null;
      newItem.isDeleteChecked = false;

      return newItem;
    });

    setUpcomingList([...newUpcomingClasses]);

    if (courseTutors.length) {
      let newTutorOptions: ISelectedOption[] = [];

      courseTutors.forEach((item) => {
        const tutorData = item.tutor;

        const name = `${tutorData.firstname || ''} ${
          tutorData.lastname || ''
        }`.trim();

        const newOption: ISelectedOption = {
          label: name,
          value: tutorData._id,
          courseTutor: item,
        };

        newTutorOptions.push(newOption);
      });

      newTutorOptions = newTutorOptions.filter(
        (item) =>
          courseTutor &&
          courseTutor.tutor &&
          item.value !== courseTutor.tutor._id
      );

      setTutorOptions(newTutorOptions);
    }
  }, [adminCourseTutorDeleteVerificationData]);

  const updateUpcomingListItemAttributes = (
    id: string,
    attributes: Partial<ICustomUpcomingClassItem>
  ) => {
    setUpcomingList((prevState) => {
      const newList = [...prevState];

      const index = newList.findIndex((item) => item._id === id);

      if (index > -1) {
        newList[index] = {
          ...newList[index],
          ...attributes,
        };
      }

      return newList;
    });
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (upcomingList.length) {
      // eslint-disable-next-line no-restricted-syntax
      for await (const section of upcomingList) {
        if (!section.successMessage) {
          if (section.isDeleteChecked) {
            updateUpcomingListItemAttributes(section._id, { isLoading: true });

            const newAttributes: Partial<ICustomUpcomingClassItem> = {
              isLoading: false,
            };

            const res: any = await dispatch(
              deleteCourseZoomSectionByIdAsAdmin(section._id)
            );

            if (res && res.payload && res.payload._id) {
              newAttributes.successMessage = 'Class deleted successfully';

              updateUpcomingListItemAttributes(section._id, newAttributes);
            } else {
              if (res && res.error) {
                const err = JSON.parse(res.error.message as string);

                if (err.data) {
                  if (err.data.error_description) {
                    newAttributes.errorMessage = err.data.error_description;
                  }
                  if (err.data.message) {
                    newAttributes.errorMessage = err.data.message;
                  }
                }
              }

              updateUpcomingListItemAttributes(section._id, newAttributes);

              break;
            }
          } else if (section.selectedTutorOption) {
            updateUpcomingListItemAttributes(section._id, { isLoading: true });

            const newAttributes: Partial<ICustomUpcomingClassItem> = {
              isLoading: false,
            };

            const res: any = await dispatch(
              updateCourseZoomSectionAsAdmin({
                sectionId: section._id,
                tutor: section.selectedTutorOption.value,
              })
            );

            if (res && res.payload && res.payload._id) {
              newAttributes.successMessage = 'Tutor changed successfully';

              dispatch(
                updateAdminCourseSectionState({
                  id: section._id,
                  section: res.payload,
                })
              );

              updateUpcomingListItemAttributes(section._id, newAttributes);
            } else {
              if (res && res.error) {
                const err = JSON.parse(res.error.message as string);

                if (err.data) {
                  if (err.data.error_description) {
                    newAttributes.errorMessage = err.data.error_description;
                  }
                  if (err.data.message) {
                    newAttributes.errorMessage = err.data.message;
                  }
                }
              }

              updateUpcomingListItemAttributes(section._id, newAttributes);

              break;
            }
          }
        }
      }
    }

    setLoading(false);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: '20px' },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: { xs: '15px', sm: '30px' },
          color: '#000000',
          fontWeight: 600,
          fontSize: '16px',
        }}
      >
        Update upcoming classes
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          right: { xs: '7px', sm: '22px' },
          top: { xs: '7px', sm: '22px' },
        }}
      >
        <CloseIcon sx={{ color: '#000000' }} />
      </IconButton>
      <DialogContent sx={{ p: { xs: '0 15px', sm: '0 30px' } }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography fontSize="14px" fontWeight={500} color="#647488">
              {modalDescription}
            </Typography>
          </Grid>
          {upcomingList.map((item, index) => {
            return (
              <Grid item xs={12} key={`upcoming-class-item-${index}`}>
                <UpcomingClassItem
                  section={item}
                  tutorOptions={tutorOptions}
                  updateUpcomingListItemAttributes={
                    updateUpcomingListItemAttributes
                  }
                />
              </Grid>
            );
          })}
          {isAllSuccess ? (
            <Grid item xs={12}>
              <Collapse in={isAllSuccess}>
                <div>
                  <Typography
                    fontSize="14px"
                    fontWeight={500}
                    color="success.main"
                  >
                    It seems all the classes have been successfully updated. You
                    may proceed to delete / disable the tutor.
                  </Typography>
                </div>
              </Collapse>
            </Grid>
          ) : null}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: { xs: '15px', sm: '30px' } }}>
        <Stack direction="row" alignItems="center" gap="10px">
          {isAllSuccess &&
          modalType === COURSE_TUTOR_VERIFY_DELETE_TYPES.DELETE ? (
            <DarkButton
              disableElevation
              variant="contained"
              color="inherit"
              sx={{ ...defaultButtonStyles }}
              onClick={() => {
                if (courseTutor) {
                  handleDeleteTutor(courseTutor);
                }
              }}
            >
              Delete Tutor
            </DarkButton>
          ) : null}
          {isAllSuccess &&
          modalType === COURSE_TUTOR_VERIFY_DELETE_TYPES.DISABLE ? (
            <DarkButton
              disableElevation
              variant="contained"
              color="inherit"
              sx={{ ...defaultButtonStyles }}
              onClick={() => {
                if (courseTutor) {
                  handleDisableTutor(courseTutor);
                }
              }}
            >
              Disable Tutor
            </DarkButton>
          ) : null}
          <DarkLoadingButton
            disableElevation
            variant="contained"
            color="inherit"
            onClick={handleSubmit}
            disabled={isSubmitDisabled}
            loading={isLoading}
            sx={{ ...defaultButtonStyles }}
          >
            Update
          </DarkLoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default CourseTutorVerificationModal;
